import { ReactNode, useState, useRef, useMemo, useEffect } from 'react'
import cn from 'classnames'

import Typography from '~/components/Typography'

import styles from './Select.pcss'

export type Option<T> = {
	label: ReactNode
	disabled?: boolean
	value: T
}

interface Props<T> {
	options: Option<T>[]
	value?: T
	placeholder?: string
	onChange?: (value: Option<T>) => void
	disabled?: boolean
}

function Select<T>({
	placeholder = 'Select option',
	options,
	value,
	onChange = () => {},
	disabled,
}: Props<T>) {
	const ref = useRef<HTMLDivElement>()
	const [isOpen, setIsOpen] = useState(false)
	const currentOption = useMemo(
		() => options.find((option) => option.value === value),
		[options, value]
	)

	const handleOpen = () => {
		if (!disabled) {
			setIsOpen(true)
		}
	}

	const handleSelect = (option: Option<T>) => {
		setIsOpen(false)
		onChange(option)
	}

	useEffect(() => {
		if (!isOpen) {
			return
		}

		const handleClickOutside = (event) => {
			if (!ref.current || ref.current.contains(event.target)) {
				return
			}

			setIsOpen(false)

			document.addEventListener(
				'click',
				(event) => {
					event.preventDefault()
					event.stopPropagation()
				},
				{ once: true, capture: true }
			)
		}

		document.addEventListener('mousedown', handleClickOutside)

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [isOpen])

	return (
		<div className={cn(styles.root, disabled && styles.disabled)} ref={ref}>
			<div className={styles.select} onClick={handleOpen}>
				{currentOption ? (
					<Typography color="white" nowrap>
						{currentOption.label}
					</Typography>
				) : (
					<Typography color="gray" nowrap>
						{placeholder}
					</Typography>
				)}
				{!disabled && (
					<svg
						fill="none"
						height="24"
						viewBox="0 0 24 24"
						width="24"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="m8 10 4 4 4-4"
							stroke="#fff"
							strokeLinecap="square"
							strokeLinejoin="bevel"
							strokeWidth="1.5"
						/>
					</svg>
				)}
			</div>

			{isOpen && (
				<div className={styles.options}>
					{options.map((item, idx) => (
						<div
							className={cn(styles.optionItem, {
								[styles.optionItemDisabled]: item.disabled,
							})}
							key={idx}
							onClick={() => !item.disabled && handleSelect(item)}
						>
							<Typography color="white" nowrap>
								{item.label}
							</Typography>
						</div>
					))}
				</div>
			)}
		</div>
	)
}

export default Select
